import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL, DEMO_MODE_CONFIG } from '../lib/api-client';
import { AtootbandanLogo } from './AtootbandanLogo';
import { BackendConnectionAlert } from './BackendConnectionAlert';
import { DemoCredentials } from './DemoCredentials';
import { handleDemoLogin } from '../utils/demoAuth';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { ArrowLeft, Mail, PhoneCall, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, themes } from './ThemeProvider';
import { useAuthContext } from '../contexts/AuthContext';
import { GoogleSignInButton } from './GoogleSignInButton';

interface LoginPageProps {
  onNavigate: (screen: any) => void;
}

export default function LoginPage({ onNavigate }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showBackendAlert, setShowBackendAlert] = useState(false);
  const [showTruecallerDialog, setShowTruecallerDialog] = useState(false);
  const [truecallerPhone, setTruecallerPhone] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { login, sendOTPForLogin, verifyOTPForLogin, truecallerLogin } = useAuthContext();

  const handleEmailLogin = async (e?: React.MouseEvent | React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsLoading(true);
    
    // Check for demo credentials first
    const demoResult = handleDemoLogin(email, password);
    if (demoResult) {
      toast.success(`🎉 Demo Login Successful! Welcome ${demoResult.user.firstName}!`);
      setTimeout(() => {
        setIsLoading(false);
        window.location.reload(); // Reload to apply demo session
      }, 500);
      return;
    }
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success('Login successful!');
        setTimeout(() => {
          setIsLoading(false);
          onNavigate('app');
        }, 500);
      } else {
        toast.error(result.error || 'Invalid email or password');
        setIsLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message || 'Invalid email or password');
      setIsLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phone) {
      toast.error('Please enter phone number');
      return;
    }
    
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await sendOTPForLogin(phone);
      
      if (result.success) {
        setOtpSent(true);
        toast.success('OTP sent successfully!');
      } else {
        toast.error(result.error || 'Failed to send OTP');
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await verifyOTPForLogin(phone, otp);
      
      if (result.success) {
        toast.success('Login successful!');
        
        setTimeout(() => {
          onNavigate('app');
        }, 150);
      } else {
        toast.error(result.error || 'OTP verification failed');
        setIsLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  const handleTruecallerLogin = async () => {
    // Show dialog to prompt for phone number
    setShowTruecallerDialog(true);
  };

  const handleTruecallerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!truecallerPhone) {
      toast.error('Please enter phone number');
      return;
    }
    
    // Validate phone number format
    const phoneDigits = truecallerPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate Truecaller SDK authentication
      // In production, this would call the actual Truecaller SDK
      const truecallerData = {
        phone: truecallerPhone,
        firstName: 'User', // In production, this comes from Truecaller SDK
        lastName: 'Truecaller', // In production, this comes from Truecaller SDK
        email: `${phoneDigits}@truecaller.com` // In production, this comes from Truecaller SDK
      };
      
      const result = await truecallerLogin(truecallerData);
      
      if (result.success) {
        toast.success('Truecaller login successful!');
        setShowTruecallerDialog(false);
        setTruecallerPhone('');
        
        // Wait for auth state to propagate
        setTimeout(() => {
          onNavigate('app');
        }, 150);
      } else {
        const errorMsg = result.error || 'Truecaller login failed';
        toast.error(errorMsg);
        setIsLoading(false);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'An error occurred during Truecaller login';
      toast.error(errorMsg);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      if (response.data.success) {
        toast.success('Password reset link sent! Check your email');
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      } else {
        toast.error(response.data.message || 'Failed to send reset link');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to send password reset link';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${currentTheme.light} flex items-center justify-center p-4`}>
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => onNavigate('home')}
          className="mb-6"
        >
          <ArrowLeft className="size-4 mr-2" />
          Back to Home
        </Button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <AtootbandanLogo size="md" showText={true} />
          </div>

          <h1 className="text-xl md:text-2xl text-center mb-2">Welcome Back</h1>
          <p className="text-center text-gray-600 mb-8">Login to find your perfect match</p>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="email">
                <Mail className="size-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="phone">
                <PhoneCall className="size-4 mr-2" />
                OTP
              </TabsTrigger>
            </TabsList>

            {/* Email/Password Login */}
            <TabsContent value="email">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleEmailLogin(e as any);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <Button 
                  type="button"
                  className={`w-full ${currentTheme.gradient} ${currentTheme.primaryHover} text-white`}
                  disabled={isLoading}
                  onClick={handleEmailLogin}
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </div>
            </TabsContent>

            {/* OTP Login */}
            <TabsContent value="phone">
              {!otpSent ? (
                <form onSubmit={handleSendOTP} className="space-y-6">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative mt-2">
                      <PhoneCall className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/\D/g, '');
                          setPhone(value);
                        }}
                        placeholder="9876543210"
                        maxLength={10}
                        className="pl-10"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter 10 digits without country code</p>
                  </div>

                  <Button 
                    type="submit" 
                    className={`w-full ${currentTheme.gradient} ${currentTheme.primaryHover} text-white`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  <div>
                    <Label htmlFor="otp">Enter OTP</Label>
                    <div className="flex justify-center mt-4">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={setOtp}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-4">
                      Sent to {phone}
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className={`w-full ${currentTheme.gradient} ${currentTheme.primaryHover} text-white`}
                    disabled={isLoading || otp.length !== 6}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Login'}
                  </Button>

                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp('');
                    }}
                    disabled={isLoading}
                  >
                    Change Number
                  </Button>
                </form>
              )}

              {!otpSent && (
                <></>
              )}
            </TabsContent>
          </Tabs>

          {/* Google Sign-In */}
          <GoogleSignInButton onSuccess={(data) => {
            if (data.isNewUser || !data.user?.profileCompleted) {
              onNavigate('complete-profile');
            } else {
              onNavigate('app');
            }
          }} />

          <div className="mt-6 text-center">
            <span className="text-gray-600">Don't have an account? </span>
            <button
              onClick={() => onNavigate('register')}
              className={`${currentTheme.primaryText} hover:underline`}
            >
              Register Free
            </button>
          </div>

          {/* Privacy Policy Link */}
          <div className="mt-4 text-center text-sm text-gray-500">
            By logging in, you agree to our{' '}
            <button
              onClick={() => onNavigate('terms')}
              className="text-blue-600 hover:underline"
            >
              Terms & Conditions
            </button>
            {' '}and{' '}
            <button
              onClick={() => onNavigate('terms')}
              className="text-blue-600 hover:underline"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      {/* Backend Connection Alert */}
      {showBackendAlert && (
        <BackendConnectionAlert onClose={() => setShowBackendAlert(false)} />
      )}

      {/* Truecaller Login Dialog */}
      <Dialog open={showTruecallerDialog} onOpenChange={setShowTruecallerDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Login with Truecaller</DialogTitle>
            <DialogDescription>
              Enter your phone number to authenticate with Truecaller.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTruecallerSubmit} className="space-y-4">
            <div>
              <Label htmlFor="truecallerPhone">Phone Number</Label>
              <div className="relative mt-2">
                <PhoneCall className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                <Input
                  id="truecallerPhone"
                  type="tel"
                  value={truecallerPhone}
                  onChange={(e) => setTruecallerPhone(e.target.value)}
                  placeholder="+91 1234567890"
                  className="pl-10"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className={`w-full ${currentTheme.gradient} ${currentTheme.primaryHover} text-white`}
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Forgot Password</DialogTitle>
            <DialogDescription>
              Enter your email address to receive a password reset link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgotPasswordEmail">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                <Input
                  id="forgotPasswordEmail"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className={`w-full ${currentTheme.gradient} ${currentTheme.primaryHover} text-white`}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Demo Credentials */}
      {DEMO_MODE_CONFIG.enabled && <DemoCredentials />}
    </div>
  );
}